import { useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Loader2 } from "lucide-react";
import {
  FACILITY_CONDITIONS,
  conditionRatedLabel,
  facilityConditionLabel,
  facilityConditionTone,
  isConditionStale,
} from "../lib/facilities";
import {
  CONDITION_UNRATED,
  conditionUpdateBody,
  conditionUpdateChanges,
  conditionUpdatePath,
} from "../lib/conditionUpdate";

/**
 * FAC-09 (issue #166) — "Condition ratings can be updated independently of
 * other fields, to reflect ongoing maintenance status without a full edit."
 *
 * ONE TAP, ONE FIELD. The rules are `lib/conditionUpdate.js`; this is the
 * surface. The person this exists for is somebody standing at the venue who
 * has just seen that the nets are torn — the correction form asks them for
 * the name, the city, the country, the place type and the pin, none of which
 * they came to change, and every one of which they would be re-submitting
 * unchecked.
 *
 * THE SAME CONTROL SERVES A VENUE AND A NET, because the API makes them the
 * same act: one vocabulary (`api/src/lib/conditionRating.ts`), one stamping
 * rule, one gate. Only the address differs, and only the address is a prop.
 * Building two would be two places to widen the vocabulary and one of them
 * would be missed — which is the argument that consolidated the vocabulary in
 * the first place.
 *
 * IT DOES NOT DECIDE WHO MAY PRESS IT. `canEdit` comes from
 * `canUpdateCondition` at the call site (the parent GROUND, even for a net),
 * and the server owns the half no client may be told — another player is
 * relying on this ground. A refusal is SHOWN, not swallowed: 403/404/409 all
 * carry prose written to be read by whoever pressed the button.
 */

/**
 * The rating badge: the word, its tone, and — always — the date it was taken.
 *
 * EXTRACTED FROM `GroundsMap.jsx` (issue #166, FAC-09). It was written once,
 * for facilities, and #164's venue rating then had no surface at all in this
 * client — the column shipped and nothing rendered it. The second reader is
 * what turns a block of JSX into a rule, and the rule here is the one #164
 * spends its whole staleness section on: THE RATING NEVER APPEARS WITHOUT ITS
 * DATE. The API sends the pair or neither; a "Good" with no date is an
 * eighteen-month-old observation reading as today's. Past
 * `CONDITION_STALE_DAYS` the age is MARKED rather than the rating hidden —
 * "good, last looked at 14 months ago" is more use than silence.
 *
 * Unrated is SAID rather than left blank. There is no sixth word for it
 * (#165), so absence is the state, and a blank where a rating goes reads as a
 * rendering failure.
 */
export function ConditionBadge({ condition, ratedAt, testId }) {
  const label = facilityConditionLabel(condition);
  const rated = conditionRatedLabel(ratedAt);
  const stale = isConditionStale(ratedAt);
  const tone = facilityConditionTone(condition);
  const toneClass =
    tone === "good" ? "bg-emerald-100 text-emerald-800"
      : tone === "fair" ? "bg-amber-100 text-amber-800"
        : tone === "bad" ? "bg-rose-100 text-rose-800"
          : "bg-slate-100 text-slate-600";
  if (!label) {
    return (
      <span
        className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-slate-100 text-slate-500"
        data-testid={testId}
      >
        Not rated
      </span>
    );
  }
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${toneClass}`} data-testid={testId}>
      {label}
      {rated && (
        <span className={stale ? "ml-1 font-normal text-slate-600" : "ml-1 font-normal opacity-80"}>
          · {rated}
          {stale ? " — may be out of date" : ""}
        </span>
      )}
    </span>
  );
}

/** The chips, in vocabulary order, plus the clear. `null` last because
 * removing a rating is the rarer act and the destructive-looking one. */
const CHOICES = [...FACILITY_CONDITIONS, CONDITION_UNRATED];

function chipLabel(value) {
  return value === CONDITION_UNRATED ? "Not rated" : facilityConditionLabel(value);
}

/**
 * `target` is `{ groundId }` for a venue or `{ groundId, facilityId }` for one
 * surface. `onUpdated` receives the SERVER'S row, never a locally-guessed
 * one: the response carries `condition_rated_at`, which only the server can
 * produce, and rendering a rating the client dated itself would be the exact
 * lie #164 exists to prevent.
 */
export default function ConditionUpdate({
  target,
  condition = null,
  ratedAt = null,
  canEdit = false,
  label = "Condition",
  testId,
  onUpdated,
}) {
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState("");
  const path = conditionUpdatePath(target);

  async function choose(next) {
    if (saving || !path) return;
    // The one tap that is not a request: clearing something already unrated.
    // Everything else goes, INCLUDING the same value again — that is the
    // seasonal re-confirmation, and the server re-dates it on purpose.
    if (!conditionUpdateChanges(condition, next)) return;
    setSaving(next === CONDITION_UNRATED ? "__clear__" : next);
    setError("");
    try {
      const res = await api.patch(path, conditionUpdateBody(next));
      if (onUpdated) onUpdated(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="mt-1.5" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <ConditionBadge condition={condition} ratedAt={ratedAt} testId={testId ? `${testId}-badge` : undefined} />
      </div>
      {canEdit && (
        <>
          <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid={testId ? `${testId}-choices` : undefined}>
            {CHOICES.map((value) => {
              const active = (condition ?? null) === value;
              const busy = saving === (value === CONDITION_UNRATED ? "__clear__" : value);
              return (
                <button
                  key={value ?? "unrated"}
                  type="button"
                  onClick={() => choose(value)}
                  disabled={!!saving}
                  aria-pressed={active}
                  data-testid={testId ? `${testId}-choice-${value ?? "unrated"}` : undefined}
                  /* 44px minimum, as every other tappable control on this page
                     carries (#134) — the primary context is a phone held at a
                     ground. */
                  className={`inline-flex items-center gap-1 rounded-md border px-2.5 min-h-11 min-w-11 text-[11px] font-semibold transition disabled:opacity-60 ${active
                    ? "border-[#0B2E59] bg-[#0B2E5908] text-[#0B2E59]"
                    : "border-slate-200 text-slate-600 hover:border-slate-400"}`}
                >
                  {busy && <Loader2 size={11} className="animate-spin" />}
                  {chipLabel(value)}
                </button>
              );
            })}
          </div>
          {/* The re-confirmation, named. Pressing the rating that is already
              set looks inert unless somebody says what it does, and it is the
              most valuable update this control makes: an old rating
              re-observed today stops reading as stale. */}
          <p className="mt-1 text-[10px] text-slate-500" data-testid={testId ? `${testId}-help` : undefined}>
            Tap to update. Choosing the rating it already has re-dates it — useful when nothing changed
            but somebody has looked again.
          </p>
        </>
      )}
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </p>
      )}
    </div>
  );
}
